import { useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Easing,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import * as SecureStore from "expo-secure-store";
import {
  createAudioPlayer,
  setAudioModeAsync,
  type AudioPlayer,
} from "expo-audio";
import type { CallerLanguage, CalleeLanguage } from "@nativecall/shared";

type DemoLang = "en" | "ja" | "zh";
type PosKey = "pos1" | "pos2" | "pos3";

const DEMO_TEXT: Record<PosKey, Record<DemoLang, string>> = {
  pos1: {
    en: "Hi, I'd like to book a table for two tomorrow at seven.",
    ja: "明日の7時に2名で予約したいのですが。",
    zh: "您好，想訂明天晚上七點，兩位。",
  },
  pos2: {
    en: "Seven thirty is available — does that work?",
    ja: "7時半なら空いております。よろしいですか？",
    zh: "七點半有位，可以嗎？",
  },
  pos3: {
    en: "Seven thirty's great, thank you.",
    ja: "7時半で大丈夫です。ありがとう。",
    zh: "七點半可以，謝謝。",
  },
};

const AUDIO: Record<PosKey, Record<DemoLang, number>> = {
  pos1: {
    en: require("../../assets/demo/pos1/en.mp3"),
    ja: require("../../assets/demo/pos1/ja.mp3"),
    zh: require("../../assets/demo/pos1/zh.mp3"),
  },
  pos2: {
    en: require("../../assets/demo/pos2/en.mp3"),
    ja: require("../../assets/demo/pos2/ja.mp3"),
    zh: require("../../assets/demo/pos2/zh.mp3"),
  },
  pos3: {
    en: require("../../assets/demo/pos3/en.mp3"),
    ja: require("../../assets/demo/pos3/ja.mp3"),
    zh: require("../../assets/demo/pos3/zh.mp3"),
  },
};

const DISMISS_KEY = "demo_banner_dismissed_v1";
const isWeb = Platform.OS === "web";

async function readDismissed(): Promise<boolean> {
  try {
    if (isWeb) {
      return window.localStorage.getItem(DISMISS_KEY) === "1";
    }
    const v = await SecureStore.getItemAsync(DISMISS_KEY);
    return v === "1";
  } catch {
    return false;
  }
}

async function writeDismissed() {
  try {
    if (isWeb) {
      window.localStorage.setItem(DISMISS_KEY, "1");
      return;
    }
    await SecureStore.setItemAsync(DISMISS_KEY, "1");
  } catch {
    /* ignore */
  }
}

type Phase =
  | { kind: "speak"; side: "caller" | "callee"; pos: PosKey }
  | { kind: "translate"; from: "caller" | "callee" }
  | { kind: "hear"; side: "caller" | "callee"; pos: PosKey }
  | { kind: "flash" };

const TIMELINE: Phase[] = [
  { kind: "speak", side: "caller", pos: "pos1" },
  { kind: "translate", from: "caller" },
  { kind: "hear", side: "callee", pos: "pos1" },
  { kind: "speak", side: "callee", pos: "pos2" },
  { kind: "translate", from: "callee" },
  { kind: "hear", side: "caller", pos: "pos2" },
  { kind: "speak", side: "caller", pos: "pos3" },
  { kind: "translate", from: "caller" },
  { kind: "hear", side: "callee", pos: "pos3" },
  { kind: "flash" },
];

const TRANSLATE_MS = 900;
const FLASH_MS = 1400;
const AUDIO_FALLBACK_MS = 3000;

type DemoCopy = {
  demoHeaderLabel: string;
  demoTapForSound: string;
  demoPlaying: string;
  demoYouLabel: string;
  demoRestaurantLabel: string;
  demoSaidTag: string;
  demoHeardTag: string;
  demoFlashCaption: string;
  demoDismissA11y: string;
};

type Props = {
  callerLanguage: CallerLanguage;
  calleeLanguage: CalleeLanguage;
  copy: DemoCopy;
};

export function TranslationDemoCard({
  callerLanguage,
  calleeLanguage,
  copy,
}: Props) {
  const [dismissed, setDismissed] = useState<boolean | null>(null);
  const [muted, setMuted] = useState(true);
  const [phaseIndex, setPhaseIndex] = useState(0);

  const playersRef = useRef<Record<string, AudioPlayer>>({});
  const currentPlayerRef = useRef<AudioPlayer | null>(null);
  const advanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mutedRef = useRef(true);

  const bubbleX = useRef(new Animated.Value(0)).current; // 0 = caller (left), 1 = callee (right)
  const bubbleOpacity = useRef(new Animated.Value(0)).current;
  const arrowOpacity = useRef(new Animated.Value(0)).current;
  const arrowProgress = useRef(new Animated.Value(0)).current;
  const callerPulse = useRef(new Animated.Value(1)).current;
  const calleePulse = useRef(new Animated.Value(1)).current;
  const flashOpacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    mutedRef.current = muted;
    if (currentPlayerRef.current) {
      try {
        currentPlayerRef.current.muted = muted;
      } catch {
        /* ignore */
      }
    }
  }, [muted]);

  useEffect(() => {
    void readDismissed().then((v) => setDismissed(v));
  }, []);

  useEffect(() => {
    if (dismissed !== false) return;
    const map: Record<string, AudioPlayer> = {};
    try {
      for (const pos of ["pos1", "pos2", "pos3"] as const) {
        for (const lang of ["en", "ja", "zh"] as const) {
          const p = createAudioPlayer(AUDIO[pos][lang]);
          p.muted = true;
          map[`${pos}-${lang}`] = p;
        }
      }
      playersRef.current = map;
    } catch {
      /* audio subsystem unavailable; animation will still loop */
    }

    void setAudioModeAsync({
      playsInSilentMode: false,
      shouldPlayInBackground: false,
    }).catch(() => {});

    return () => {
      Object.values(map).forEach((p) => {
        try {
          p.pause();
        } catch {
          /* ignore */
        }
        try {
          (p as AudioPlayer & { release?: () => void }).release?.();
        } catch {
          /* ignore */
        }
      });
      playersRef.current = {};
      currentPlayerRef.current = null;
    };
  }, [dismissed]);

  useEffect(() => {
    if (dismissed !== false) return;
    const phase = TIMELINE[phaseIndex];
    const cleanupFns: Array<() => void> = [];

    if (phase.kind === "speak" || phase.kind === "hear") {
      const lang = phase.side === "caller" ? callerLanguage : calleeLanguage;
      const key = `${phase.pos}-${lang}`;
      const player = playersRef.current[key];

      Animated.parallel([
        Animated.timing(bubbleX, {
          toValue: phase.side === "caller" ? 0 : 1,
          duration: 250,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.timing(bubbleOpacity, {
          toValue: 1,
          duration: 220,
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.timing(arrowOpacity, {
          toValue: 0,
          duration: 150,
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.timing(flashOpacity, {
          toValue: 0,
          duration: 150,
          useNativeDriver: Platform.OS !== "web",
        }),
      ]).start();

      const pulse = phase.side === "caller" ? callerPulse : calleePulse;
      const pulseLoop = Animated.loop(
        Animated.sequence([
          Animated.timing(pulse, {
            toValue: 1.12,
            duration: 380,
            useNativeDriver: Platform.OS !== "web",
          }),
          Animated.timing(pulse, {
            toValue: 1,
            duration: 380,
            useNativeDriver: Platform.OS !== "web",
          }),
        ]),
      );
      pulseLoop.start();
      cleanupFns.push(() => pulseLoop.stop());

      const advance = () => setPhaseIndex((i) => (i + 1) % TIMELINE.length);

      let resolved = false;
      const resolveOnce = () => {
        if (resolved) return;
        resolved = true;
        advance();
      };

      let durationMs = AUDIO_FALLBACK_MS;
      if (player) {
        try {
          player.muted = mutedRef.current;
          player.seekTo(0);
          Promise.resolve(player.play()).catch(() => {
            /* web: AbortError (tab hidden/pause race) and NotAllowedError (autoplay gate) are expected */
          });
          currentPlayerRef.current = player;
          const d = player.duration;
          if (typeof d === "number" && d > 0 && isFinite(d)) {
            durationMs = Math.max(1200, d * 1000 + 200);
          }
          const sub = player.addListener?.(
            "playbackStatusUpdate",
            (status: { didJustFinish?: boolean }) => {
              if (status?.didJustFinish) {
                sub?.remove?.();
                resolveOnce();
              }
            },
          );
          if (sub) cleanupFns.push(() => sub.remove?.());
        } catch {
          /* fall back to timeout-based advance */
        }
      }

      const t = setTimeout(resolveOnce, durationMs + 400);
      cleanupFns.push(() => clearTimeout(t));
      advanceTimeoutRef.current = t;
    } else if (phase.kind === "translate") {
      arrowProgress.setValue(phase.from === "caller" ? 0 : 1);
      Animated.parallel([
        Animated.timing(arrowOpacity, {
          toValue: 1,
          duration: 180,
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.timing(bubbleOpacity, {
          toValue: 0.15,
          duration: 180,
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.timing(arrowProgress, {
          toValue: phase.from === "caller" ? 1 : 0,
          duration: TRANSLATE_MS,
          easing: Easing.inOut(Easing.cubic),
          useNativeDriver: Platform.OS !== "web",
        }),
      ]).start();
      const t = setTimeout(() => {
        setPhaseIndex((i) => (i + 1) % TIMELINE.length);
      }, TRANSLATE_MS);
      cleanupFns.push(() => clearTimeout(t));
    } else {
      // flash
      Animated.parallel([
        Animated.timing(bubbleOpacity, {
          toValue: 0,
          duration: 300,
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.timing(arrowOpacity, {
          toValue: 0,
          duration: 200,
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.sequence([
          Animated.timing(flashOpacity, {
            toValue: 1,
            duration: 300,
            useNativeDriver: Platform.OS !== "web",
          }),
          Animated.delay(FLASH_MS - 600),
          Animated.timing(flashOpacity, {
            toValue: 0,
            duration: 300,
            useNativeDriver: Platform.OS !== "web",
          }),
        ]),
      ]).start();
      const t = setTimeout(() => {
        setPhaseIndex((i) => (i + 1) % TIMELINE.length);
      }, FLASH_MS);
      cleanupFns.push(() => clearTimeout(t));
    }

    return () => {
      cleanupFns.forEach((fn) => {
        try {
          fn();
        } catch {
          /* ignore */
        }
      });
      if (currentPlayerRef.current) {
        try {
          currentPlayerRef.current.pause();
        } catch {
          /* ignore */
        }
      }
    };
  }, [dismissed, phaseIndex, callerLanguage, calleeLanguage]);

  const handleDismiss = async () => {
    setDismissed(true);
    await writeDismissed();
  };

  const handleTap = () => {
    setMuted((m) => !m);
  };

  const bubble = useMemo(() => {
    const phase = TIMELINE[phaseIndex];
    if (phase.kind === "speak" || phase.kind === "hear") {
      const lang = phase.side === "caller" ? callerLanguage : calleeLanguage;
      return {
        text: DEMO_TEXT[phase.pos][lang],
        lang,
        tone: phase.kind === "speak" ? ("said" as const) : ("heard" as const),
        side: phase.side,
      };
    }
    if (phase.kind === "translate") {
      const nextPhase = TIMELINE[(phaseIndex + 1) % TIMELINE.length];
      if (nextPhase.kind === "hear") {
        const lang =
          nextPhase.side === "caller" ? callerLanguage : calleeLanguage;
        return {
          text: DEMO_TEXT[nextPhase.pos][lang],
          lang,
          tone: "heard" as const,
          side: nextPhase.side,
        };
      }
    }
    return null;
  }, [phaseIndex, callerLanguage, calleeLanguage]);

  if (dismissed !== false) return null;

  const bubbleLeftPct = bubbleX.interpolate({
    inputRange: [0, 1],
    outputRange: ["6%", "44%"],
  });
  const arrowLeftPct = arrowProgress.interpolate({
    inputRange: [0, 1],
    outputRange: ["22%", "60%"],
  });

  return (
    <Pressable onPress={handleTap} style={styles.card} accessibilityRole="button">
      <View style={styles.headerRow}>
        <View style={styles.headerLeft}>
          <View style={styles.liveDot} />
          <Text style={styles.headerLabel}>{copy.demoHeaderLabel}</Text>
        </View>
        <View style={styles.headerRight}>
          <View style={styles.soundHint}>
            <Text style={styles.soundHintText}>
              {muted ? copy.demoTapForSound : copy.demoPlaying}
            </Text>
          </View>
          <Pressable
            onPress={handleDismiss}
            hitSlop={12}
            accessibilityLabel={copy.demoDismissA11y}
            style={styles.dismissBtn}
          >
            <Text style={styles.dismissBtnText}>×</Text>
          </Pressable>
        </View>
      </View>

      <View style={styles.stage}>
        <View style={[styles.sideColumn, styles.sideLeft]}>
          <Animated.View
            style={[styles.avatar, { transform: [{ scale: callerPulse }] }]}
          >
            <Text style={styles.avatarEmoji}>{callerEmoji(callerLanguage)}</Text>
          </Animated.View>
          <Text style={styles.sideLabel}>{copy.demoYouLabel}</Text>
          <Text style={styles.sideSubLabel}>
            {langLabel(callerLanguage)}
          </Text>
        </View>

        <View style={styles.centerColumn}>
          <Animated.View
            style={[
              styles.arrow,
              {
                opacity: arrowOpacity,
                left: arrowLeftPct,
              },
            ]}
          >
            <Text style={styles.arrowText}>→</Text>
          </Animated.View>
        </View>

        <View style={[styles.sideColumn, styles.sideRight]}>
          <Animated.View
            style={[styles.avatar, { transform: [{ scale: calleePulse }] }]}
          >
            <Text style={styles.avatarEmoji}>{calleeEmoji(calleeLanguage)}</Text>
          </Animated.View>
          <Text style={styles.sideLabel}>{copy.demoRestaurantLabel}</Text>
          <Text style={styles.sideSubLabel}>
            {langLabel(calleeLanguage)}
          </Text>
        </View>

        {bubble ? (
          <Animated.View
            style={[
              styles.bubble,
              bubble.tone === "said" ? styles.bubbleSaid : styles.bubbleHeard,
              {
                left: bubbleLeftPct,
                opacity: bubbleOpacity,
              },
            ]}
            pointerEvents="none"
          >
            <Text
              style={[
                styles.bubbleText,
                bubble.tone === "said"
                  ? styles.bubbleTextSaid
                  : styles.bubbleTextHeard,
              ]}
              numberOfLines={3}
            >
              {bubble.text}
            </Text>
            <Text
              style={[
                styles.bubbleTag,
                bubble.tone === "said"
                  ? styles.bubbleTagSaid
                  : styles.bubbleTagHeard,
              ]}
            >
              {bubble.tone === "said" ? copy.demoSaidTag : copy.demoHeardTag} ·{" "}
              {bubble.lang.toUpperCase()}
            </Text>
          </Animated.View>
        ) : null}

        <Animated.View
          pointerEvents="none"
          style={[styles.flashOverlay, { opacity: flashOpacity }]}
        >
          <Text style={styles.flashText}>{copy.demoFlashCaption}</Text>
        </Animated.View>
      </View>
    </Pressable>
  );
}

function callerEmoji(lang: CallerLanguage) {
  return lang === "zh" ? "🇨🇳" : "🇬🇧";
}

function calleeEmoji(lang: CalleeLanguage) {
  return "🇯🇵";
}

function langLabel(lang: CallerLanguage | CalleeLanguage) {
  switch (lang) {
    case "en":
      return "English";
    case "zh":
      return "中文";
    case "ja":
      return "日本語";
    default:
      return lang;
  }
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: "rgba(255, 250, 241, 0.94)",
    borderRadius: 28,
    padding: 16,
    borderWidth: 1,
    borderColor: "#e3d7c7",
    shadowColor: "#8f826c",
    shadowOpacity: 0.08,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    overflow: "hidden",
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  liveDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#e63946",
  },
  headerLabel: {
    color: "#0f172a",
    fontWeight: "700",
    fontSize: 13,
    letterSpacing: 0.3,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  soundHint: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: "#eadfce",
    borderRadius: 12,
  },
  soundHintText: {
    color: "#4c5c59",
    fontSize: 11,
    fontWeight: "600",
  },
  dismissBtn: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#eadfce",
  },
  dismissBtnText: {
    color: "#4c5c59",
    fontSize: 18,
    lineHeight: 20,
    marginTop: -2,
    fontWeight: "600",
  },
  stage: {
    height: 160,
    position: "relative",
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
  },
  sideColumn: {
    width: "30%",
    alignItems: "center",
    paddingBottom: 2,
  },
  sideLeft: {
    alignItems: "center",
  },
  sideRight: {
    alignItems: "center",
  },
  centerColumn: {
    flex: 1,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#fff",
    borderWidth: 2,
    borderColor: "#e3d7c7",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 4,
  },
  avatarEmoji: {
    fontSize: 26,
  },
  sideLabel: {
    fontSize: 12,
    fontWeight: "700",
    color: "#0f172a",
  },
  sideSubLabel: {
    fontSize: 10,
    color: "#7c8a86",
    marginTop: 1,
  },
  bubble: {
    position: "absolute",
    top: 0,
    width: "50%",
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 8,
    shadowColor: "#000",
    shadowOpacity: 0.05,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
  },
  bubbleSaid: {
    backgroundColor: "#f1e9dc",
    borderWidth: 1,
    borderColor: "#d9cdb4",
  },
  bubbleHeard: {
    backgroundColor: "#1f3a8a",
  },
  bubbleText: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "600",
  },
  bubbleTextSaid: {
    color: "#4c5c59",
  },
  bubbleTextHeard: {
    color: "#ffffff",
  },
  bubbleTag: {
    fontSize: 9,
    marginTop: 4,
    fontWeight: "700",
    letterSpacing: 0.8,
  },
  bubbleTagSaid: {
    color: "#8f826c",
  },
  bubbleTagHeard: {
    color: "#c7d1f0",
  },
  arrow: {
    position: "absolute",
    top: 36,
    alignItems: "center",
    justifyContent: "center",
  },
  arrowText: {
    fontSize: 22,
    color: "#1f3a8a",
    fontWeight: "800",
  },
  flashOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255, 250, 241, 0.9)",
    borderRadius: 14,
  },
  flashText: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: "800",
    textAlign: "center",
    paddingHorizontal: 12,
  },
});
